import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BaseComponent,
  ListComponent,
  SearchResult,
  SnackbarService,
  SnackbarType,
} from 'src/app/shared';
import {
  InventoryAttachment,
  InventoryItem,
} from '../../models/inventory/inventory-item';
import {
  InventoryItemStatus,
  isDeleted,
  statusAfterAttachmentChange,
} from '../../models/inventory/inventory-item-status';
import { createInventoryTranslationRequest } from '../../models/inventory/inventory-translation-request';
import { emulator } from '../../services/emulator.service';
import { InventoryService } from '../../services/inventory/inventory.service';
import {
  InventoryAddAttachmentsDialogComponent,
  InventoryAddAttachmentsDialogData,
} from './inventory-add-attachments-dialog.component';
import { ItemTab } from './inventory-item-detail.component';

type DeleteTarget =
  | { type: 'item'; itemId: number }
  | { type: 'attachment'; itemId: number; attachment: InventoryAttachment };

@Component({
  selector: 'app-inventory-items-list',
  template: `
    <app-list
      #itemsList
      [dataSource]="visibleItems"
      [template]="row"
      [filterEnabled]="visibleItems.length > 0"
      [forceHeader]="visibleItems.length > 0"
      [paginatorEnabled]="true"
      [searchLabel]="'Search results for…'"
      class="app-pt2"
      noDataIcon="attachment"
      noDataHeader=""
      [noDataMessage]="noDataMessage"
      [loaded]="true"
      [tableAriaLabel]="'Items'"
      DefaultSortColumn="dateUploaded"
      DefaultSortDir="desc"
      [staticSortLabel]="true"
    >
      <ng-container header>
        <tr class="tw-flex tw-flex-wrap tw-w-full tw-items-center">
          <app-list-header
            label=""
            readOnly
            class="tw-w-full md:tw-w-[4%]"
          ></app-list-header>
          <app-list-header
            name="itemName"
            label="Item"
            class="tw-w-full md:tw-w-[14%]"
          ></app-list-header>
          <app-list-header
            name="quantity"
            label="QTY"
            class="tw-w-full md:tw-w-[8%]"
          ></app-list-header>
          <app-list-header
            name="cost"
            label="Cost"
            class="tw-w-full md:tw-w-[12%]"
          ></app-list-header>
          <app-list-header
            name="attachmentCount"
            label="Attachments"
            class="tw-w-full md:tw-w-[12%]"
          ></app-list-header>
          <app-list-header
            name="dateUploaded"
            label="Date uploaded"
            class="tw-w-full md:tw-w-[18%]"
          ></app-list-header>
          @if (showActions) {
            <app-list-header
              label="Actions"
              readOnly
              class="tw-w-full md:tw-w-[12%]"
            ></app-list-header>
          }
        </tr>
      </ng-container>
    </app-list>

    <ng-template #row let-element>
      <div class="tw-w-full">
        <div
          class="tw-flex tw-flex-wrap tw-w-full tw-items-center app-pt2 app-pb2"
        >
          <div class="tw-w-full md:tw-w-[4%] tw-flex tw-items-center">
            <app-icon-button
              [icon]="
                expandedItems[element.id] ? 'expand_more' : 'chevron_right'
              "
              [label]="
                expandedItems[element.id]
                  ? 'Collapse item details'
                  : 'Expand item details'
              "
              tabindex="0"
              role="button"
              [ariaLabelText]="
                expandedItems[element.id]
                  ? 'Collapse item details'
                  : 'Expand item details'
              "
              (click)="toggleExpand(element.id, $event)"
              (keydown.enter)="toggleExpand(element.id, $event)"
            ></app-icon-button>
          </div>
          <div class="tw-w-full md:tw-w-[14%] inventory-cell">
            <app-value
              label="Item"
              [value]="element.itemName"
              layout="row-xs"
              [translationNeeded]="false"
            ></app-value>
          </div>
          <div class="tw-w-full md:tw-w-[8%] inventory-cell">
            <app-value
              label="QTY"
              [value]="element.quantity"
              layout="row-xs"
              [translationNeeded]="false"
            ></app-value>
          </div>
          <div class="tw-w-full md:tw-w-[12%] inventory-cell">
            <app-value
              label="Cost"
              [value]="element.cost | currency"
              layout="row-xs"
              [translationNeeded]="false"
            ></app-value>
          </div>
          <div class="tw-w-full md:tw-w-[12%] inventory-cell">
            <app-value
              label="Attachments"
              layout="row-xs"
              [translationNeeded]="false"
              [valueTemplate]="attachmentCountTpl"
            >
              <ng-template #attachmentCountTpl>
                <span class="attachment-count">{{
                  attachmentCountDisplay(element)
                }}</span>
              </ng-template>
            </app-value>
          </div>
          <div class="tw-w-full md:tw-w-[18%] inventory-cell">
            <app-value
              label="Date uploaded"
              [value]="element.dateUploaded | format: 'dateTime'"
              layout="row-xs"
              [translationNeeded]="false"
            ></app-value>
          </div>
          @if (showActions) {
            <div class="tw-w-full md:tw-w-[12%] inventory-actions-cell">
              <app-value
                label="Actions"
                layout="row-xs"
                [valueTemplate]="actionsTpl"
              >
              <ng-template #actionsTpl>
                <div class="inventory-action-links tw-flex tw-flex-col tw-items-start tw-gap-2 md:tw-flex-row md:tw-flex-wrap md:tw-items-center md:tw-gap-x-4 md:tw-gap-y-2">
                  <app-icon-button
                    icon="attach_file"
                    label="Add attachments"
                    tabindex="0"
                    role="button"
                    ariaLabelText="Add attachments"
                    [disabled]="readOnly"
                    (click)="openAttachmentsDialog(element)"
                    (keydown.enter)="openAttachmentsDialog(element)"
                  ></app-icon-button>
                  <app-icon-button
                    icon="edit"
                    label="Edit item"
                    tabindex="0"
                    role="button"
                    ariaLabelText="Edit item"
                    [disabled]="isEditDisabled(element.id)"
                    (click)="onEditItem(element)"
                    (keydown.enter)="onEditItem(element)"
                  ></app-icon-button>
                  <app-icon-button
                    icon="delete_outline"
                    label="Delete item"
                    tabindex="0"
                    role="button"
                    ariaLabelText="Delete item"
                    [disabled]="readOnly"
                    (click)="confirmDeleteItem(element.id)"
                    (keydown.enter)="confirmDeleteItem(element.id)"
                  ></app-icon-button>
                </div>
              </ng-template>
            </app-value>
            </div>
          }
        </div>

        @if (initializedItems[element.id]) {
          <div [hidden]="!expandedItems[element.id]">
            <app-inventory-item-detail
              [item]="element"
              [readOnly]="readOnly"
              [activeTab]="selectedTabs[element.id] ?? 'details'"
              (tabChange)="rememberTab(element.id, $event)"
              (removeAttachment)="confirmDeleteAttachment(element.id, $event)"
            ></app-inventory-item-detail>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #deleteConfirmationTemplate>
      <app-dialog
        title="Confirmation"
        ariaLabel="Delete confirmation dialog"
        [confirmLabel]="'Confirm'"
        [cancelLabel]="'Cancel'"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
        (confirmEvent)="deleteConfirmed($event)"
      >
        {{ 'Press Confirm to delete Item.' | translation: translationRequest }}
      </app-dialog>
    </ng-template>
  `,
  styles: [
    `
      .inventory-cell {
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .inventory-cell ::ng-deep .row-xs-label {
        white-space: nowrap;
        flex-shrink: 0;
      }

      @media (max-width: 767px) {
        .inventory-actions-cell ::ng-deep app-value > div {
          align-items: flex-start;
        }

        .inventory-action-links app-icon-button {
          display: block;
        }
      }

      .attachment-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 22px;
        height: 22px;
        padding: 0 6px;
        border-radius: 999px;
        background-color: var(--sed-sedgwick-green-color);
        color: var(--sed-dynamic-black-color);
        font-size: 12px;
        font-weight: bold;
        line-height: 1;
      }
    `,
  ],
  standalone: false,
})
export class InventoryItemsListComponent
  extends BaseComponent
  implements AfterViewInit
{
  @ViewChild('itemsList')
  itemsList: ListComponent;

  @ViewChild('deleteConfirmationTemplate')
  deleteConfirmationTemplate: TemplateRef<any>;

  private _items: InventoryItem[] = [];
  visibleItems: InventoryItem[] = [];

  @Input()
  set items(value: InventoryItem[]) {
    this._items = value ?? [];
    for (const item of this._items) {
      item.attachments = sortAttachmentsNewestFirst(item.attachments);
    }
    this.visibleItems = sortItemsNewestFirst(
      this._items.filter((item) => !isDeleted(item.status)),
    );
  }
  get items(): InventoryItem[] {
    return this._items;
  }

  @Input()
  editingItemId: number | null = null;

  @Input()
  source: string;

  @Input()
  claimId: string;

  @Input()
  claim: SearchResult;

  @Input()
  readOnly = false;

  @Input()
  showActions = true;

  @Output()
  itemsChange = new EventEmitter<InventoryItem[]>();

  @Output()
  editItem = new EventEmitter<InventoryItem>();

  @Output()
  inventoryModified = new EventEmitter<void>();

  expandedItems: Record<number, boolean> = {};
  initializedItems: Record<number, boolean> = {};
  selectedTabs: Record<number, ItemTab> = {};
  filterText = '';
  private pendingDelete: DeleteTarget | null = null;
  private attachmentFocusItemId: number | null = null;
  private attachmentFocusIndex: Record<number, number> = {};

  translationRequest = createInventoryTranslationRequest();

  constructor(
    private dialog: MatDialog,
    private inventoryService: InventoryService,
    private emulatorService: emulator,
    private snackbarService: SnackbarService,
    private host: ElementRef<HTMLElement>,
  ) {
    super();
  }

  ngAfterViewInit(): void {
    if (this.itemsList?.textFilterControl) {
      this.subs.sink = this.itemsList.textFilterControl.valueChanges.subscribe(
        (value) => {
          this.filterText = value ?? '';
        },
      );
    }
  }

  get noDataMessage(): string {
    return this.visibleItems?.length && this.filterText
      ? 'No items. Use the Search feature to find an item'
      : 'No items added for this inventory.';
  }

  attachmentCountDisplay(item: InventoryItem): string {
    return String(item.attachmentCount ?? 0);
  }

  isEditDisabled(itemId: number): boolean {
    return (
      this.readOnly ||
      (this.editingItemId !== null && this.editingItemId !== itemId)
    );
  }

  rememberTab(itemId: number, tab: ItemTab): void {
    this.selectedTabs[itemId] = tab;
  }

  toggleExpand(itemId: number, event?: Event): void {
    event?.stopPropagation();
    const expanding = !this.expandedItems[itemId];
    this.expandedItems[itemId] = expanding;

    if (expanding) {
      this.initializedItems[itemId] = true;
    }
  }

  onEditItem(item: InventoryItem): void {
    if (this.isEditDisabled(item.id)) {
      return;
    }
    this.editItem.emit(item);
  }

  confirmDeleteItem(itemId: number): void {
    if (this.readOnly) {
      return;
    }
    this.pendingDelete = { type: 'item', itemId };
    this.dialog.open(this.deleteConfirmationTemplate);
  }

  confirmDeleteAttachment(
    itemId: number,
    attachment: InventoryAttachment,
  ): void {
    if (this.readOnly) {
      return;
    }

    const trigger =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    this.attachmentFocusItemId = null;
    this.pendingDelete = { type: 'attachment', itemId, attachment };
    const dialogRef = this.dialog.open(this.deleteConfirmationTemplate, {
      restoreFocus: false,
    });

    this.subs.sink = dialogRef.afterClosed().subscribe(() => {
      if (this.attachmentFocusItemId === itemId) {
        this.focusAttachmentsSection(itemId);
        return;
      }

      if (trigger?.isConnected) {
        trigger.focus();
      }
    });
  }

  async deleteConfirmed(event: string): Promise<void> {
    const target = this.pendingDelete;
    this.pendingDelete = null;

    if (event !== 'confirm' || !target) {
      return;
    }

    if (this.emulatorService.showAuditorInfo()) {
      return;
    }

    const updatedItems = this.items.map((item) => ({
      ...item,
      attachments: [...(item.attachments ?? [])],
    }));
    const itemIndex = updatedItems.findIndex(
      (item) => item.id === target.itemId,
    );

    if (itemIndex < 0) {
      return;
    }

    let focusAttachments = false;
    let changed = false;

    if (target.type === 'item') {
      changed = await this.deleteItem(updatedItems, itemIndex);
    } else {
      this.attachmentFocusItemId = target.itemId;
      this.selectedTabs[target.itemId] = 'attachments';
      focusAttachments = true;
      changed = await this.deleteAttachment(
        updatedItems,
        itemIndex,
        target.attachment,
      );
    }

    if (changed) {
      this.items = updatedItems;
      this.itemsChange.emit(updatedItems);
      this.inventoryModified.emit();
      this.itemsList?.updateDataSource();
    }

    if (focusAttachments) {
      this.focusAttachmentsSection(target.itemId);
    }
  }

  openAttachmentsDialog(item: InventoryItem): void {
    if (this.readOnly) {
      return;
    }

    const dialogRef = this.dialog.open(InventoryAddAttachmentsDialogComponent, {
      data: {
        item,
        source: this.source,
        claimId: this.claimId,
        lob: this.claim.lob,
        claim: this.claim,
      } as InventoryAddAttachmentsDialogData,
      width: '700px',
    });

    this.subs.sink = dialogRef.afterClosed().subscribe((result) => {
      if (!Array.isArray(result) || result.length === 0) {
        return;
      }

      const attachments = result as InventoryAttachment[];

      const updatedItems = this.items.map((existing) => {
        if (existing.id !== item.id) {
          return existing;
        }

        const merged = [...existing.attachments, ...attachments];

        return {
          ...existing,
          attachments: merged,
          attachmentCount: merged.length,
          status: statusAfterAttachmentChange(existing.status),
        };
      });

      this.items = updatedItems;
      this.itemsChange.emit(updatedItems);
      this.inventoryModified.emit();
      this.itemsList?.updateDataSource();
    });
  }

  private async deleteItem(
    items: InventoryItem[],
    itemIndex: number,
  ): Promise<boolean> {
    const removed = items[itemIndex];
    const wasPreviouslySubmitted =
      removed.status === InventoryItemStatus.existing ||
      removed.status === InventoryItemStatus.updated;

    if (wasPreviouslySubmitted) {
      items[itemIndex] = { ...removed, status: InventoryItemStatus.deleted };
      this.forgetExpansion(removed.id);

      return true;
    }

    const failed = await this.deleteAttachmentsOnServer(
      removed.attachments ?? [],
    );

    if (failed.length > 0) {
      items[itemIndex] = {
        ...removed,
        attachments: failed,
        attachmentCount: failed.length,
      };
      this.snackbarService.show(
        'This item could not be fully removed. Please try again.',
        SnackbarType.Error,
      );

      return true;
    }

    items.splice(itemIndex, 1);
    this.forgetExpansion(removed.id);

    return true;
  }

  private async deleteAttachment(
    items: InventoryItem[],
    itemIndex: number,
    attachment: InventoryAttachment,
  ): Promise<boolean> {
    const item = { ...items[itemIndex] };
    const removedIndex = item.attachments.findIndex((candidate) =>
      this.isSameAttachment(candidate, attachment),
    );
    const removed =
      removedIndex >= 0 ? item.attachments[removedIndex] : undefined;

    if (!removed) {
      return false;
    }

    this.attachmentFocusIndex[item.id] = removedIndex;

    const failed = await this.deleteAttachmentsOnServer([removed]);

    if (failed.length > 0) {
      this.snackbarService.show(
        'That attachment could not be removed. Please try again.',
        SnackbarType.Error,
      );

      return false;
    }

    item.attachments = item.attachments.filter(
      (candidate) => !this.isSameAttachment(candidate, removed),
    );
    item.attachmentCount = item.attachments.length;
    item.status = statusAfterAttachmentChange(item.status);
    items[itemIndex] = item;

    return true;
  }

  private isSameAttachment(
    left: InventoryAttachment,
    right: InventoryAttachment,
  ): boolean {
    return left.attachmentId && right.attachmentId
      ? left.attachmentId === right.attachmentId
      : left === right;
  }

  private async deleteAttachmentsOnServer(
    attachments: InventoryAttachment[],
  ): Promise<InventoryAttachment[]> {
    const deletions = attachments
      .filter((attachment) => attachment.attachmentId)
      .map(async (attachment): Promise<InventoryAttachment | null> => {
        try {
          const response = await this.inventoryService.deleteAttachment(
            this.source,
            this.claimId,
            this.claim.lob,
            attachment.attachmentId,
            this.claim,
          );

          return response?.success ? null : attachment;
        } catch {
          return attachment;
        }
      });

    const results = await Promise.all(deletions);

    return results.filter(
      (attachment): attachment is InventoryAttachment => attachment !== null,
    );
  }

  private forgetExpansion(itemId: number): void {
    delete this.expandedItems[itemId];
    delete this.initializedItems[itemId];
    delete this.selectedTabs[itemId];
    delete this.attachmentFocusIndex[itemId];
  }

  private focusAttachmentsSection(itemId: number): void {
    const attempt = () => {
      const panel = this.host.nativeElement.querySelector<HTMLElement>(
        `#inventory-attachments-panel-${itemId}`,
      );

      if (!panel) {
        return false;
      }

      const removeButtons = panel.querySelectorAll<HTMLElement>(
        'app-icon-button[tabindex="0"]',
      );
      const focusIndex = this.attachmentFocusIndex[itemId] ?? 0;
      const removeButton =
        removeButtons[Math.min(focusIndex, removeButtons.length - 1)];
      const target = removeButton ?? panel;
      target.focus();

      return (
        document.activeElement === target || panel.contains(document.activeElement)
      );
    };

    setTimeout(() => {
      attempt();
      setTimeout(attempt, 200);
    });
  }
}

function sortItemsNewestFirst(items: InventoryItem[]): InventoryItem[] {
  if (items.length < 2) {
    return items;
  }

  const sorted = [...items].sort((left, right) => {
    const byDate =
      new Date(right.dateUploaded).getTime() -
      new Date(left.dateUploaded).getTime();
    if (byDate !== 0 && !Number.isNaN(byDate)) {
      return byDate;
    }

    return right.id - left.id;
  });

  const alreadySorted = sorted.every((item, index) => item === items[index]);
  return alreadySorted ? items : sorted;
}

function sortAttachmentsNewestFirst(
  attachments: InventoryAttachment[] | undefined,
): InventoryAttachment[] {
  const source = attachments ?? [];
  if (source.length < 2) {
    return source;
  }

  const sorted = [...source].sort((left, right) => {
    const byDate =
      new Date(right.dateUploaded).getTime() -
      new Date(left.dateUploaded).getTime();
    if (byDate !== 0 && !Number.isNaN(byDate)) {
      return byDate;
    }

    return (right.attachmentId ?? 0) - (left.attachmentId ?? 0);
  });

  const alreadySorted = sorted.every(
    (attachment, index) => attachment === source[index],
  );
  return alreadySorted ? source : sorted;
}
